import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const MobileCardSlider = ({ children, className = '', showDots = true, autoScroll = false }) => {
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const childrenArray = React.Children.toArray(children);
  const totalCards = childrenArray.length;

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
      
      // Calculate current index based on scroll position
      const cardWidth = container.scrollWidth / totalCards;
      const index = Math.round(container.scrollLeft / cardWidth);
      setCurrentIndex(index);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      checkScroll();
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, []);

  useEffect(() => {
    if (autoScroll && scrollContainerRef.current) {
      const interval = setInterval(() => {
        const container = scrollContainerRef.current;
        if (container) {
          const maxScroll = container.scrollWidth - container.clientWidth;
          if (container.scrollLeft >= maxScroll - 10) {
            container.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollToNext();
          }
        }
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [autoScroll]);

  const scrollToNext = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const cardWidth = container.scrollWidth / totalCards;
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const scrollToPrev = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const cardWidth = container.scrollWidth / totalCards;
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    }
  };

  const scrollToIndex = (index) => {
    const container = scrollContainerRef.current;
    if (container) {
      const cardWidth = container.scrollWidth / totalCards;
      container.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      {/* Scrollable Container */}
      <div
        ref={scrollContainerRef}
        className={`flex overflow-x-auto scrollbar-hide snap-x snap-mandatory gap-4 pb-4 ${className}`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {childrenArray.map((child, index) => (
          <div
            key={index}
            className="flex-shrink-0 snap-center"
            style={{ width: 'calc(100% - 2rem)' }}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      {canScrollLeft && (
        <Button
          onClick={scrollToPrev}
          variant="outline"
          size="icon"
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 rounded-full bg-white shadow-lg hover:bg-secondary/10 z-10"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      )}

      {canScrollRight && (
        <Button
          onClick={scrollToNext}
          variant="outline"
          size="icon"
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 rounded-full bg-white shadow-lg hover:bg-secondary/10 z-10"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      )}

      {/* Dots Indicator */}
      {showDots && totalCards > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {childrenArray.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-secondary w-6' : 'bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileCardSlider;
